import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import HlcFooter from "../components/HlcFooter";
import { useDispatch, useSelector } from "react-redux";
import { addToCart, incrementQty, decrementQty } from "../redux/cartSlice";
//import { getCutoutImage } from "../utils/getCutoutImage";
import { getProductImage } from "../utils/getProductImage";
import { API_BASE_URL } from "../config/apiConfig";
import { cssVars } from "../theme";
import ProductCard from "../components/shop/ProductCard";
import QuickViewModal from "../components/shop/QuickViewModal";
import { WhatsAppIcon, ShieldIcon, TruckIcon, CheckIcon } from "../components/shop/ShopIcons";
import "../styles/hlcShopSystem.css";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.items);
  const getCartItem = (pid) => cartItems.find((item) => item.id === pid);

  const [allProducts, setAllProducts] = useState([]);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const [selectedWatt, setSelectedWatt] = useState(null);
  const [selectedPinType, setSelectedPinType] = useState(null);
  const [selectedColourType, setSelectedColourType] = useState(null);
  const [selectedsize, setSelectedsize] = useState(null);

  const [qvOpen, setQvOpen] = useState(false);
  const [qvProduct, setQvProduct] = useState(null);

  useEffect(() => {
    if (!product) return;

    if (Array.isArray(product.wattColourModel) && product.wattColourModel.length > 0) {
      setSelectedWatt(product.wattColourModel[0]);
    }
    if (Array.isArray(product.pinType) && product.pinType.length > 0) {
      setSelectedPinType(product.pinType[0]);
    }
    if (Array.isArray(product.size) && product.size.length > 0) {
      setSelectedsize(product.size[0]);
    }
    if (Array.isArray(product.colourType) && product.colourType.length > 0) {
      setSelectedColourType(product.colourType[0]);
    }
  }, [product]);

  // Fetch product from adminhlc-api (MySQL) — same endpoint/lookup as
  // before (no single-product admin endpoint exists). Also keeps the
  // full list around now (allProducts) so a real "You may also like"
  // section can be built from actual catalog data instead of invented
  // products.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    fetch(`${API_BASE_URL}/api/public/products/list.php`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        const parsed = Array.isArray(data) ? data : [];
        setAllProducts(parsed);

        const match = parsed.find((p) => String(p.id) === String(id));
        if (match) {
          setProduct(match);
          if (Array.isArray(match.wattColourModel) && match.wattColourModel.length > 0) {
            setSelectedWatt(match.wattColourModel[0]);
          }
        } else {
          setProduct(null);
        }

        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch product:", err);
        if (!cancelled) {
          setProduct(null);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    return allProducts
      .filter((p) => String(p.id) !== String(product.id) && p.category === product.category)
      .slice(0, 4);
  }, [allProducts, product]);

  const handleAddToCart = () => {
    if (!product) return;
    dispatch(addToCart({ ...product, selectedWatt }));
  };

  const openQuickView = (p) => { setQvProduct(p); setQvOpen(true); };
  const closeQuickView = () => { setQvOpen(false); setQvProduct(null); };
  const viewProduct = (pid) => {
    closeQuickView();
    navigate(`/product/${pid}`);
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div id="hlc-pd-page" className="hlc-shop-system" style={cssVars}>
          <div className="hlc-shop__loading" style={{ padding: "140px 20px 60px" }}>Loading product…</div>
        </div>
        <HlcFooter />
      </>
    );
  }

  if (!product) {
    return (
      <>
        <Navbar />
        <div id="hlc-pd-page" className="hlc-shop-system" style={cssVars}>
          <div className="hlc-shop__loading" style={{ padding: "140px 20px 60px" }}>
            Product not found.
            <div style={{ marginTop: 18 }}>
              <button type="button" className="hlc-pd__addcart" style={{ display: "inline-flex" }} onClick={() => navigate("/shop")}>
                Back to Shop
              </button>
            </div>
          </div>
        </div>
        <HlcFooter />
      </>
    );
  }

  //const image = getCutoutImage(product);
  const image = getProductImage(product);
  const hasDiscount = Boolean(product.discountedPrice);
  const inStock = Number(product.stock) > 0;
  const cartItem = getCartItem(product.id);

  return (
    <>
      <Navbar />
      <div style={{ marginTop: "var(--nav-h, 74px)" }} />

      <div id="hlc-pd-page" className="hlc-shop-system hlc-pd" style={cssVars}>
        <div className="hlc-pd__back">
          <button type="button" onClick={() => navigate("/shop")}>← Back to Shop</button>
        </div>

        <div className="hlc-pd__hero">
          <div className="hlc-pd__gallery">
            <img src={image} alt={product.name} />
          </div>

          <div className="hlc-pd__info">
            {product.category && <span className="hlc-pd__eyebrow">{product.category}</span>}
            <h1>{product.name}</h1>

            <div className="hlc-pd__price-row">
              <span className="hlc-pd__price">Rs {product.discountedPrice || product.price}</span>
              {hasDiscount && <span className="hlc-pd__price-old">Rs {product.price}</span>}
              {hasDiscount && product.discount && <span className="hlc-pd__discount-tag">{product.discount}</span>}
            </div>

            {product.stock !== undefined && product.stock !== null && product.stock !== "" && (
              <span className={`hlc-pd__stock ${inStock ? "in" : "out"}`}>
                <span className="hlc-pd__stock-dot" /> {inStock ? "In Stock" : "Out of Stock"}
              </span>
            )}

            <div className="hlc-pd__variants">
              {product.wattColourModel?.length > 0 && (
                <div className="hlc-pd__variant-group">
                  <label>Watt / Colour / Model</label>
                  <div className="hlc-pd__variant-buttons">
                    {product.wattColourModel.map((item, i) => (
                      <button key={i} type="button" className={selectedWatt === item ? "active" : ""} onClick={() => setSelectedWatt(item)}>
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {product.pinType?.length > 0 && (
                <div className="hlc-pd__variant-group">
                  <label>Pin Type</label>
                  <div className="hlc-pd__variant-buttons">
                    {product.pinType.map((item, i) => (
                      <button key={i} type="button" className={selectedPinType === item ? "active" : ""} onClick={() => setSelectedPinType(item)}>
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {product.size?.length > 0 && (
                <div className="hlc-pd__variant-group">
                  <label>Size</label>
                  <div className="hlc-pd__variant-buttons">
                    {product.size.map((item, i) => (
                      <button key={i} type="button" className={selectedsize === item ? "active" : ""} onClick={() => setSelectedsize(item)}>
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {product.colourType?.length > 0 && (
                <div className="hlc-pd__variant-group">
                  <label>Colour Type</label>
                  <div className="hlc-pd__variant-buttons">
                    {product.colourType.map((item, i) => (
                      <button key={i} type="button" className={selectedColourType === item ? "active" : ""} onClick={() => setSelectedColourType(item)}>
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="hlc-pd__actions">
              {!cartItem ? (
                <button type="button" className="hlc-pd__addcart" onClick={handleAddToCart}>
                  Add to Cart
                </button>
              ) : (
                <div className="hlc-qv-counter" style={{ minHeight: 48 }}>
                  <button type="button" onClick={() => dispatch(decrementQty(product.id))}>-</button>
                  <span>{cartItem.quantity || 1}</span>
                  <button type="button" onClick={() => dispatch(incrementQty(product.id))}>+</button>
                </div>
              )}

              <a
                href={`https://wa.me/923702961896?text=I%20am%20interested%20in%20${encodeURIComponent(product.name)}%20-%20PKR%20${product.price}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hlc-pd__whatsapp"
              >
                <WhatsAppIcon /> WhatsApp Inquiry
              </a>
            </div>
          </div>
        </div>

        {product.description && (
          <section className="hlc-pd__section hlc-pd__description">
            <h2>Description</h2>
            <p>{product.description}</p>
          </section>
        )}

        <section className="hlc-pd__section">
          <h2>Specifications</h2>
          <div className="hlc-pd__specs">
            {product.category && (
              <div className="hlc-pd__spec"><span className="hlc-pd__spec-label">Category</span><span className="hlc-pd__spec-value">{product.category}</span></div>
            )}
            {product.subcategory && (
              <div className="hlc-pd__spec"><span className="hlc-pd__spec-label">Subcategory</span><span className="hlc-pd__spec-value">{product.subcategory}</span></div>
            )}
            {selectedWatt && (
              <div className="hlc-pd__spec"><span className="hlc-pd__spec-label">Watt / Model</span><span className="hlc-pd__spec-value">{selectedWatt}</span></div>
            )}
            {selectedColourType && (
              <div className="hlc-pd__spec"><span className="hlc-pd__spec-label">Colour Type</span><span className="hlc-pd__spec-value">{selectedColourType}</span></div>
            )}
            {selectedPinType && (
              <div className="hlc-pd__spec"><span className="hlc-pd__spec-label">Pin Type</span><span className="hlc-pd__spec-value">{selectedPinType}</span></div>
            )}
            {selectedsize && (
              <div className="hlc-pd__spec"><span className="hlc-pd__spec-label">Size</span><span className="hlc-pd__spec-value">{selectedsize}</span></div>
            )}
            {product.stock !== undefined && product.stock !== null && product.stock !== "" && (
              <div className="hlc-pd__spec"><span className="hlc-pd__spec-label">Stock</span><span className="hlc-pd__spec-value">{product.stock}</span></div>
            )}
          </div>
        </section>

        <section className="hlc-pd__section">
          <h2>Delivery &amp; Warranty</h2>
          <div className="hlc-pd__perks">
            <div className="hlc-pd__perk">
              <span className="hlc-pd__perk-icon"><ShieldIcon /></span>
              <div><h4>1-Year Warranty</h4><p>Peace of mind, always.</p></div>
            </div>
            <div className="hlc-pd__perk">
              <span className="hlc-pd__perk-icon"><TruckIcon /></span>
              <div><h4>Nationwide Delivery</h4><p>Fast &amp; reliable across Pakistan.</p></div>
            </div>
            <div className="hlc-pd__perk">
              <span className="hlc-pd__perk-icon"><CheckIcon /></span>
              <div><h4>Genuine HLC Product</h4><p>Sourced directly from HLC.</p></div>
            </div>
          </div>
        </section>

        {relatedProducts.length > 0 && (
          <section className="hlc-pd__section">
            <h2>You May Also Like</h2>
            <div className="hlc-pd__related-grid">
              {relatedProducts.map((p, idx) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  idx={idx}
                  cartItem={getCartItem(p.id)}
                  onAddToCart={(prod) => dispatch(addToCart(prod))}
                  onIncrement={(pid) => dispatch(incrementQty(pid))}
                  onDecrement={(pid) => dispatch(decrementQty(pid))}
                  onQuickView={openQuickView}
                  onView={(pid) => navigate(`/product/${pid}`)}
                />
              ))}
            </div>
          </section>
        )}
      </div>

      <HlcFooter />

      <QuickViewModal
        product={qvProduct}
        open={qvOpen}
        onClose={closeQuickView}
        cartItem={qvProduct ? getCartItem(qvProduct.id) : null}
        onAddToCart={(p) => dispatch(addToCart(p))}
        onIncrement={(pid) => dispatch(incrementQty(pid))}
        onDecrement={(pid) => dispatch(decrementQty(pid))}
        onViewFull={viewProduct}
      />
    </>
  );
}
